import { BACKEND_ERROR_CODE, REQUEST_ID_KEY, createFlatRequest, createRequest } from '@sa/axios';
import { useAuthStore } from '@/store/modules/auth';
import { $t } from '@/locales';
import { localStg } from '@/utils/storage';
import { getServiceBaseURL } from '@/utils/service';
import {
  appUuid,
  backendBusinessErrorCodeObj,
  backendErrorCodeObj,
  bigDataUrlList,
  fileUrlList,
  tenantUuid
} from '@/constants/apiConfig';
import { useAppStore } from '@/store/modules/app';
import { useGetSign } from '@/hooks/common/sign';
import {
  dateCenterList,
  filterChangeableRequest,
  getAuthorizationToken,
  getIsNoCrossService,
  getUserDataService,
  isNoErrorTip,
  tokenExpirationCode,
  updateAuthorizationToken,
  whiteRequest
} from '@/store/modules/data-center/shared';
import { getUserInfo } from '@/store/modules/auth/shared';
import type { RequestInstanceState } from './type';

const SIXTY_SECONDS = 60 * 1000;
const TWENTY_MINS = 20 * 60 * 1000;
const isHttpProxy = import.meta.env.DEV && import.meta.env.VITE_HTTP_PROXY === 'Y';

const { baseURL, otherBaseURL } = getServiceBaseURL(import.meta.env, isHttpProxy);
let dialogShow: boolean = false;

function handleLogout() {
  const authStore = useAuthStore();
  authStore.resetStore();
}
function logoutAndCleanup() {
  dialogShow = false;
  handleLogout();
  window.removeEventListener('beforeunload', handleLogout);
}
// 错误信息通过弹窗形式显示 403-退出登录
function showTokenExpire(statusCode: number, message: string) {
  const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
  if (modalLogoutCodes.includes(statusCode.toString())) {
    // 防止用户刷新页面
    window.addEventListener('beforeunload', handleLogout);
    if (!dialogShow) {
      window.$dialog?.error({
        title: $t('common.tip'),
        content: $t(message),
        positiveText: $t('common.ensure'),
        closable: false,
        closeOnEsc: false,
        maskClosable: false,
        onPositiveClick() {
          logoutAndCleanup();
        }
      });
    }
    dialogShow = true;
    return true;
  }
}

export const request = createFlatRequest<App.Service.Response, RequestInstanceState>(
  {
    baseURL: '',
    headers: {}
  },
  {
    /** 请求发送之前执行，用来修改请求配置，例如：添加请求头 token */
    async onRequest(config) {
      const appStore = useAppStore();
      const { headers } = config;
      headers['Accept-Language'] = appStore.locale;

      config.params ??= {};
      Object.assign(config.params, {
        tenantUuid,
        appUuid,
        // 本地调试
        // tenantId:1,
        // appId:18,
        ts: new Date().getTime()
      });

      // 超时时间重置
      if (fileUrlList.includes(config.url as string)) {
        config.timeout = TWENTY_MINS;
      }
      if (bigDataUrlList.includes(config.url as string)) {
        config.timeout = SIXTY_SECONDS;
      }

      if (!whiteRequest.includes(config.url as string)) {
        headers.Authorization = getAuthorizationToken();
        headers.SpaceUuid = getUserInfo()?.tenantUuid;

        if (!getIsNoCrossService() && config.url?.includes('usercenter/api')) {
          config.headers['Session-Id'] = `${localStg.get('token')}#uc`;
        }

        if (
          !dateCenterList.includes(config.url as string) &&
          !config.url?.includes('http') &&
          !getIsNoCrossService() &&
          !filterChangeableRequest(config.url as string)
        ) {
          config.url = `${getUserDataService() ? getUserDataService()?.appApiAddress : ''}${config.url}`;
        }
      }

      config.params = useGetSign(config.params);
      // console.log('config',config)
      return config;
    },
    /** 判断后端响应是否成功，通过对比后端返回的 code 来判断 */
    isBackendSuccess(response) {
      return String(response.data.statusCode) === import.meta.env.VITE_SERVICE_SUCCESS_CODE;
    },
    /** 后端请求在业务上表示失败时调用的异步函数，例如：处理 token 过期 */
    async onBackendFail(response, instance) {
      const status: number = response.status;
      const backendErrorCode: number = response.data.statusCode || 500;
      const success: boolean = response.data.success;
      // 重新获取token
      if (tokenExpirationCode.includes(backendErrorCode) && !request.state.isRefreshingToken) {
        // 先不加锁操作
        // request.state.isRefreshingToken = true;

        const { error } = await updateAuthorizationToken();

        // request.state.isRefreshingToken = false;
        if (!error) {
          response.config.params ??= {};
          Object.assign(response.config.params, {
            tenantUuid,
            appUuid,
            ts: new Date().getTime()
          });

          return instance.request(response.config);
        }

        // 报错不走下面
        return null;
      }

      if (status === 200 && backendErrorCode !== 200 && !success && !isNoErrorTip(response.config.url as string)) {
        const message = backendBusinessErrorCodeObj[backendErrorCode];
        if (message) {
          // 错误信息通过弹窗形式显示  退出登录  401-token is Expired
          const flag = showTokenExpire(backendErrorCode, message) || false;
          if (!flag) {
            window.$message?.error($t(message), { keepAliveOnHover: true });
          }
        } else {
          window.$message?.error($t(backendBusinessErrorCodeObj[500]), { keepAliveOnHover: true });
          // window.$message?.error(`${$t('backendErrorCode.miss')}  statusCode=[${backendErrorCode}]`, {
          //   keepAliveOnHover: true
          // }); // 测试用
        }
      }

      return null;
    },
    /** 当 responseType 为 json 时，转换后端响应的数据 */
    transformBackendResponse(response) {
      return response.data.result;
    },

    onError(error) {
      // 当请求失败时，可以在这里处理显示错误信息的逻辑
      const status = error.response?.status;
      const backendErrorCode = status || 500;
      const message = backendErrorCodeObj[backendErrorCode];
      if (message && !isNoErrorTip(error.response?.config.url as string)) {
        // 其他错误code全局提示
        window.$message?.error($t(message), { keepAliveOnHover: true });
      }
    }
  }
);

export const otherRequest = createRequest<App.Service.DemoResponse>(
  {
    baseURL: ''
  },
  {
    async onRequest(config) {
      return config;
    },
    isBackendSuccess(response) {
      return String(response.data.statusCode) === '200';
    },
    async onBackendFail(_response) {},
    transformBackendResponse(response) {
      return response.data.result;
    },
    onError(error) {
      let message = error.message;

      // show backend error message
      if (error.code === BACKEND_ERROR_CODE) {
        message = error.response?.data?.message || message;
      }

      window.$message?.error(message);
    }
  }
);
